import { movies } from "@/mocks/movies";
import { newsList } from "@/mocks/news";
import type { MediaItem, MediaType, NewsItem } from "@/types/media";

export const typeLabel: Record<MediaType, string> = {
  movie: "电影",
  tv: "电视剧",
};

export type SortKey = "latest" | "popular" | "rating";

export function getMediaById(id: string): MediaItem | undefined {
  return movies.find((m) => m.id === id);
}

export function getMediaByType(type: MediaType): MediaItem[] {
  return movies.filter((m) => m.type === type);
}

export function getFeatured(ids: string[]): MediaItem[] {
  return ids
    .map((id) => getMediaById(id))
    .filter((m): m is MediaItem => Boolean(m));
}

export function getPopular(limit = 6): MediaItem[] {
  return [...movies].sort((a, b) => b.clicks - a.clicks).slice(0, limit);
}

export function getRelated(item: MediaItem, limit = 5): MediaItem[] {
  return movies
    .filter(
      (m) =>
        m.id !== item.id &&
        (m.type === item.type ||
          m.genres.some((g) => item.genres.includes(g))),
    )
    .sort((a, b) => b.clicks - a.clicks)
    .slice(0, limit);
}

export function sortMedia(list: MediaItem[], sort: SortKey): MediaItem[] {
  const copy = [...list];
  if (sort === "popular") return copy.sort((a, b) => b.clicks - a.clicks);
  if (sort === "rating") return copy.sort((a, b) => b.rating - a.rating);
  return copy.sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
  );
}

export function filterMedia(
  source: MediaItem[],
  opts: { region: string; year: string; genre: string },
): MediaItem[] {
  return source.filter((m) => {
    if (opts.region !== "全部" && m.region !== opts.region) return false;
    if (opts.year !== "全部") {
      if (opts.year === "更早") {
        if (m.year >= 2000) return false;
      } else if (String(m.year) !== opts.year) {
        return false;
      }
    }
    if (opts.genre !== "全部" && !m.genres.includes(opts.genre)) return false;
    return true;
  });
}

export function searchMedia(keyword: string): MediaItem[] {
  const k = keyword.trim().toLowerCase();
  if (!k) return [];
  return movies.filter(
    (m) =>
      m.title.toLowerCase().includes(k) ||
      m.director.toLowerCase().includes(k) ||
      m.actors.some((a) => a.toLowerCase().includes(k)) ||
      m.genres.some((g) => g.toLowerCase().includes(k)) ||
      m.tags.some((t) => t.toLowerCase().includes(k)) ||
      m.synopsis.toLowerCase().includes(k),
  );
}

export function searchNews(keyword: string): NewsItem[] {
  const k = keyword.trim().toLowerCase();
  if (!k) return [];
  return newsList.filter(
    (n) =>
      n.title.toLowerCase().includes(k) ||
      n.summary.toLowerCase().includes(k) ||
      n.tags.some((t) => t.toLowerCase().includes(k)) ||
      n.category.toLowerCase().includes(k),
  );
}

export function getNewsById(id: string): NewsItem | undefined {
  return newsList.find((n) => n.id === id);
}

export function getRelatedNews(item: NewsItem, limit = 3): NewsItem[] {
  return newsList
    .filter((n) => n.id !== item.id)
    .sort((a, b) => {
      const sameA = a.category === item.category ? 1 : 0;
      const sameB = b.category === item.category ? 1 : 0;
      if (sameA !== sameB) return sameB - sameA;
      return new Date(b.date).getTime() - new Date(a.date).getTime();
    })
    .slice(0, limit);
}

export function formatClicks(n: number): string {
  if (n >= 10000) return `${(n / 10000).toFixed(1)}万`;
  return `${n}`;
}